import LazyMap from "@/components/site/LazyMap";
import { Link } from "@/lib/router-compat";
import {
  MapPin,
  Check,
  Shield,
  Phone,
  Lock,
  Car,
  Clock,
  Monitor,
  Calendar,
  Pill,
  Network,
} from "lucide-react";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import InsurancePills from "@/components/site/InsurancePills";
import RelatedLinks from "@/components/site/RelatedLinks";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const CANONICAL =
  "https://cherryhillrecoverycenter.com/addiction-treatment-glassboro-nj";
const TITLE =
  "Addiction Treatment Glassboro NJ | Cherry Hill Rehab";
const DESCRIPTION =
  "Addiction treatment near Glassboro NJ — PHP, IOP, MAT and detox referral at Cherry Hill Recovery Center. Most insurance accepted. Call 856-200-3127.";

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Helvetica, Arial, sans-serif";

type Pill = { label: string; href: string };

const programs: Pill[] = [
  { label: "Virtual IOP", href: "/virtual-iop-new-jersey" },
  { label: "Psychiatric Evaluation", href: "/psychiatric-evaluation-nj" },
  { label: "Drug & Alcohol Evaluation", href: "/drug-alcohol-evaluation-nj" },
];

const conditions: Pill[] = [
  { label: "Heroin & Opioid Addiction", href: "/heroin-opioid-addiction-treatment-nj" },
  { label: "Alcohol Addiction", href: "/alcohol-addiction-treatment-cherry-hill-nj" },
  { label: "Fentanyl Addiction", href: "/fentanyl-addiction-treatment-nj" },
  { label: "Prescription Painkillers", href: "/painkiller-addiction-treatment-nj" },
  { label: "Benzodiazepine Addiction", href: "/benzodiazepine-addiction-treatment-nj" },
  { label: "Cocaine Addiction", href: "/cocaine-addiction-treatment-nj" },
  { label: "Marijuana Addiction", href: "/marijuana-addiction-treatment-nj" },
  { label: "Adderall & Stimulants", href: "/adderall-addiction-treatment-nj" },
  { label: "Methadone", href: "/methadone-addiction-treatment-nj" },
  { label: "Synthetic Drugs (K2, Zaza)", href: "/synthetic-drug-treatment-nj" },
  { label: "Dual Diagnosis", href: "/psychiatric-evaluation-nj" },
];

const insurers: string[] = [
  "Aetna",
  "Anthem",
  "Blue Cross Blue Shield",
  "Cigna",
  "United Healthcare",
  "Humana",
  "Magellan",
  "Beacon",
  "Compsych",
  "Health Net",
  "Independence Administrators",
  "Carefirst",
  "& More",
];

const nearby: Pill[] = [
  { label: "Pitman", href: "/service-areas" },
  { label: "Mullica Hill", href: "/service-areas" },
  { label: "Williamstown", href: "/service-areas" },
  { label: "Wenonah", href: "/service-areas" },
  { label: "Woodbury", href: "/service-areas" },
  { label: "Sewell", href: "/addiction-treatment-sewell-nj" },
  { label: "Washington Township", href: "/addiction-treatment-washington-township-nj" },
  { label: "Gloucester County", href: "/service-areas" },
];

const faqs = [
  {
    q: "Is Cherry Hill Recovery Center close to Glassboro NJ?",
    a: "Cherry Hill Recovery Center is located approximately 18 miles from Glassboro via Route 322 and Route 295, approximately a 25 to 30 minute drive depending on traffic. We also offer Virtual IOP for Gloucester County patients — including Rowan University students — who prefer telehealth from home.",
  },
  {
    q: "Does insurance cover addiction treatment for Glassboro residents?",
    a: "Yes — most major HMO, PPO, and EPO plans cover PHP and IOP treatment regardless of whether the facility is located in Gloucester County or an adjacent county. Many university student health plans also provide coverage. Our admissions team verifies your coverage before you begin at no cost to you.",
  },
  {
    q: "Do you offer evening or virtual IOP for Glassboro patients and Rowan University students?",
    a: "Yes — Evening IOP is available for patients balancing work or coursework during the day, and Virtual IOP via telehealth is available for students and residents who prefer to attend from home or their dorm in Glassboro.",
  },
  {
    q: "What is the difference between PHP and IOP?",
    a: "PHP — Partial Hospitalization Program — provides five to six days per week of intensive treatment, typically five to eight hours per day. IOP — Intensive Outpatient Program — provides three to four days per week, approximately three hours per session, allowing patients to maintain work and family responsibilities. The right level is determined by a free clinical assessment our team conducts before treatment begins.",
  },
  {
    q: "How do I get started from Glassboro?",
    a: "Call our admissions team at 856-200-3127 — available 24 hours a day. We complete a free clinical assessment by phone in under 15 minutes and can in many cases schedule admission within 24 to 48 hours. Most major insurance accepted.",
  },
];

const PillLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "inline-flex",
      alignItems: "center",
      background: "#FFFFFF",
      border: "1px solid #F0E0E0",
      borderRadius: 50,
      padding: "10px 20px",
      color: "#8B1A1A",
      fontWeight: 700,
      fontSize: 13,
      margin: 5,
      textDecoration: "none",
      fontFamily: SANS,
    }}
  >
    {children}
  </Link>
);

const SidebarLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "block",
      color: "#8B1A1A",
      fontSize: 14,
      textDecoration: "none",
      padding: "6px 0",
    }}
  >
    {children}
  </Link>
);

const AddictionTreatmentGlassboroNJ = () => {
  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Cherry Hill Recovery Center",
    telephone: "+1-856-200-3127",
    url: CANONICAL,
    address: {
      "@type": "PostalAddress",
      streetAddress: "1930 Marlton Pike East",
      addressLocality: "Cherry Hill",
      addressRegion: "NJ",
      postalCode: "08003",
      addressCountry: "US",
    },
    areaServed: {
      "@type": "City",
      name: "Glassboro",
      containedIn: {
        "@type": "AdministrativeArea",
        name: "Gloucester County, NJ",
      },
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cherryhillrecoverycenter.com/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://cherryhillrecoverycenter.com/service-areas" },
      { "@type": "ListItem", position: 3, name: "Addiction Treatment Glassboro NJ", item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={[clinicSchema, faqSchema, breadcrumbSchema]} />
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" style={{ background: "#FAF4F0", padding: "12px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", fontSize: 13, color: "#2C2C2C", fontFamily: SANS }}>
          <Link to="/" style={{ color: "#8B1A1A", textDecoration: "none" }}>Home</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <Link to="/service-areas" style={{ color: "#8B1A1A", textDecoration: "none" }}>Service Areas</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <span>Addiction Treatment Glassboro NJ</span>
        </div>
      </nav>

      {/* HERO */}
      <header
        style={{
          background:
            "linear-gradient(90deg, #6B1414 0%, #8B1A1A 100%), radial-gradient(circle at 20% 30%, rgba(250,244,240,0.06) 1px, transparent 1px)",
          backgroundSize: "auto, 24px 24px",
          padding: "96px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center", width: "100%" }}>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              marginBottom: 12,
              fontFamily: SANS,
              fontWeight: 600,
            }}
          >
            Serving Glassboro, NJ
          </p>
          <h1
            style={{
              fontFamily: SERIF,
              fontSize: 40,
              color: "#FFFFFF",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Addiction Treatment Near <span style={{ color: "#FAC8C8" }}>Glassboro, NJ</span>
          </h1>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 17,
              margin: "12px auto 24px",
              maxWidth: 600,
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            Cherry Hill Recovery Center provides PHP, IOP, MAT, and detox referral for patients from Glassboro and surrounding Gloucester County communities.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE}
            </a>
            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Verify Your Insurance
            </Link>
          </div>
        </div>
      </header>

      {/* TRUST BAR */}
      <div
        style={{
          background: "#FAF4F0",
          borderTop: "3px solid #8B1A1A",
          borderBottom: "1px solid #F0E0E0",
          padding: "16px 0",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: 13,
            color: "#2C2C2C",
            textAlign: "center",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <MapPin size={16} color="#8B1A1A" /> 18 Miles from Glassboro
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Check size={16} color="#8B1A1A" /> Most Insurance Accepted
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Shield size={16} color="#8B1A1A" /> Licensed & Accredited
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Phone size={16} color="#8B1A1A" /> Available 24/7
          </span>
        </div>
      </div>

      {/* SECTION 2 — LOCAL INTRO */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 65fr) minmax(0, 35fr)",
            gap: 40,
          }}
          className="local-intro-grid"
        >
          <div>
            <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, lineHeight: 1.2 }}>
              Addiction Treatment Serving Glassboro and Gloucester County
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 18, fontFamily: SANS }}>
              If you or someone you love is struggling with addiction in Glassboro or the surrounding Gloucester County area — help is available just 18 miles away. Cherry Hill Recovery Center is accessible from Glassboro via Route 322 and Route 295, offering{" "}
              <Link to="/php-cherry-hill-nj" style={{ color: "#8B1A1A" }}>Partial Hospitalization</Link>,{" "}
              <Link to="/iop-cherry-hill-nj" style={{ color: "#8B1A1A" }}>Intensive Outpatient</Link>,{" "}
              <Link to="/medication-assisted-treatment-nj" style={{ color: "#8B1A1A" }}>Medication-Assisted Treatment</Link>, and{" "}
              <Link to="/detox-referral-coordination-nj" style={{ color: "#8B1A1A" }}>detox referral</Link>{" "}
              to patients from across Gloucester County, including Rowan University students and the broader community. Most major insurance accepted. Free confidential assessments available 24 hours a day at 856-200-3127.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Addiction and Recovery in Gloucester County
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              Glassboro is home to Rowan University and is one of the largest municipalities in Gloucester County, New Jersey. With a significant student and young adult population alongside long-term residents, Glassboro reflects the broader Gloucester County trend of rising substance use disorder cases — particularly involving opioids and stimulants — while having limited accredited outpatient treatment options within the immediate area. We also serve nearby Pitman, Mullica Hill, Williamstown, Wenonah, Woodbury, {" "}
              <Link to="/addiction-treatment-sewell-nj" style={{ color: "#8B1A1A" }}>Sewell</Link>, and{" "}
              <Link to="/addiction-treatment-washington-township-nj" style={{ color: "#8B1A1A" }}>Washington Township</Link>.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Why Glassboro Residents Choose Cherry Hill Recovery Center
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              For Glassboro patients, the combination of proximity and clinical depth is what sets Cherry Hill Recovery Center apart. Our program is led by Dr. Jeffrey Simon, a board-certified psychiatrist specializing in addiction and dual-diagnosis treatment — comprehensive clinical oversight not always available locally in Gloucester County. Patients — including students balancing coursework — can attend{" "}
              <Link to="/evening-iop-new-jersey" style={{ color: "#8B1A1A" }}>Evening IOP</Link>{" "}
              after classes or work, or attend from home or their dorm via{" "}
              <Link to="/virtual-iop-new-jersey" style={{ color: "#8B1A1A" }}>Virtual IOP</Link>{" "}
              — no commute required. Not sure what level of care is right?{" "}
              <Link to="/find-your-level-of-care" style={{ color: "#8B1A1A" }}>Take our free quiz</Link>.
            </p>
          </div>

          {/* SIDEBAR */}
          <aside
            style={{
              position: "sticky",
              top: 90,
              alignSelf: "start",
              background: "#FAF4F0",
              border: "1px solid #F0E0E0",
              borderTop: "4px solid #8B1A1A",
              borderRadius: 12,
              padding: 28,
              fontFamily: SANS,
            }}
          >
            <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: 0 }}>Get Help Today</h3>
            <p style={{ color: "#2C2C2C", fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>
              Free confidential assessment. Most insurance accepted. Same-day response.
            </p>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                display: "block",
                background: "#8B1A1A",
                color: "#FAF4F0",
                padding: "12px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                marginTop: 14,
                textAlign: "center",
              }}
            >
              Call {PHONE}
            </a>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                display: "block",
                background: "transparent",
                color: "#8B1A1A",
                border: "2px solid #8B1A1A",
                padding: "10px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Verify My Insurance
            </Link>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Programs Available:</p>
            <div style={{ marginTop: 8 }}>
              <SidebarLink to="/php-cherry-hill-nj">→ Partial Hospitalization (PHP)</SidebarLink>
              <SidebarLink to="/iop-cherry-hill-nj">→ Intensive Outpatient (IOP)</SidebarLink>
              <SidebarLink to="/virtual-iop-new-jersey">→ Virtual IOP & Telehealth</SidebarLink>
              <SidebarLink to="/medication-assisted-treatment-nj">→ Medication Assisted Treatment</SidebarLink>
              <SidebarLink to="/detox-referral-coordination-nj">→ Detox Referral & Coordination</SidebarLink>
              <SidebarLink to="/psychiatric-evaluation-nj">→ Psychiatric Evaluations</SidebarLink>
            </div>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Our Location:</p>
            <p style={{ color: "#888", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              1930 Marlton Pike East
              <br />
              Cherry Hill, NJ 08003
            </p>
            <a
              href="https://maps.google.com/maps?daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none" }}
            >
              Get Directions →
            </a>
          </aside>
        </div>
      </section>

      {/* SECTION 3 — DISTANCE & ACCESS */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontFamily: SERIF,
              color: "#8B1A1A",
              fontSize: 28,
              textAlign: "center",
              margin: 0,
              marginBottom: 32,
            }}
          >
            Why Glassboro Residents Choose Cherry Hill Recovery Center
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 20,
            }}
          >
            {[
              {
                Icon: MapPin,
                title: "18 Miles from Glassboro",
                body:
                  "Accessible from Glassboro via Route 322 and Route 295 — a manageable commute for Gloucester County residents seeking an accredited outpatient program.",
                small: "1930 Marlton Pike East, Cherry Hill, NJ 08003",
              },
              {
                Icon: Shield,
                title: "Board-Certified Psychiatric Care",
                body:
                  "Led by Dr. Jeffrey Simon, a board-certified psychiatrist specializing in addiction and dual-diagnosis treatment — comprehensive clinical oversight not always available locally in Gloucester County.",
              },
              {
                Icon: Clock,
                title: "Evening IOP Available",
                body:
                  "Glassboro patients — including students balancing coursework — can attend Evening IOP after classes or work, with the same clinical quality as our daytime program.",
              },
              {
                Icon: Monitor,
                title: "Virtual IOP Available Statewide",
                body:
                  "Prefer to attend from home or your dorm in Glassboro? Our Virtual IOP program is available to patients across New Jersey via telehealth — no commute required.",
              },
            ].map(({ Icon, title, body, small }) => (
              <div
                key={title}
                style={{
                  background: "#FFFFFF",
                  borderRadius: 12,
                  padding: 24,
                  borderTop: "3px solid #8B1A1A",
                  textAlign: "center",
                  boxShadow: "0 6px 18px rgba(139,26,26,0.06)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={24} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 18, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>{body}</p>
                {small && (
                  <p style={{ color: "#888", fontSize: 12, marginTop: 10 }}>{small}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — PROGRAMS */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 32, textAlign: "center", margin: 0 }}>
            Programs Available to Glassboro and Gloucester County Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 17,
              maxWidth: 700,
              margin: "16px auto 36px",
              textAlign: "center",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Programs available to Glassboro and Gloucester County residents — most insurance plans accepted. Our admissions team verifies your benefits before anything starts.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 22,
            }}
          >
            {[
              {
                Icon: Calendar,
                title: "Partial Hospitalization Program (PHP)",
                body:
                  "Five to six days per week of intensive outpatient treatment. Individual therapy, group counseling, psychiatric evaluation, and medication management. Patients from Glassboro attend daily and return home each evening.",
                link: "/php-cherry-hill-nj",
                linkText: "Learn About PHP →",
              },
              {
                Icon: Clock,
                title: "Intensive Outpatient Program (IOP)",
                body:
                  "Three to four days per week of structured treatment. Evening and virtual scheduling available for Glassboro patients who need to maintain work, school, or family responsibilities.",
                link: "/iop-cherry-hill-nj",
                linkText: "Learn About IOP →",
              },
              {
                Icon: Pill,
                title: "Medication Assisted Treatment (MAT)",
                body:
                  "Suboxone and Sublocade for opioid use disorder — integrated into PHP or IOP when clinically appropriate and agreed upon by the clinical team and patient. Never provided as a standalone prescription.",
                link: "/medication-assisted-treatment-nj",
                linkText: "Learn About MAT →",
              },
              {
                Icon: Network,
                title: "Detox Referral & Coordination",
                body:
                  "We assess each patient's needs and refer them to the right medically supervised detox provider — coordinating the process and transitioning patients directly into PHP or IOP upon completion.",
                link: "/detox-referral-coordination-nj",
                linkText: "Learn About Detox Referral →",
              },
            ].map(({ Icon, title, body, link, linkText }) => (
              <div
                key={title}
                style={{
                  background: "#FAF4F0",
                  borderRadius: 12,
                  padding: 28,
                  borderTop: "4px solid #8B1A1A",
                  boxShadow: "0 6px 18px rgba(107,20,20,0.08)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={26} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 19, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.65, marginTop: 10 }}>{body}</p>
                <Link
                  to={link}
                  style={{
                    display: "inline-block",
                    color: "#8B1A1A",
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    marginTop: 12,
                  }}
                >
                  {linkText}
                </Link>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: 28 }}>
            {programs.map((p) => (
              <PillLink key={p.href} to={p.href}>
                {p.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — CONDITIONS */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, textAlign: "center", margin: 0 }}>
            Conditions We Treat
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              textAlign: "center",
              maxWidth: 760,
              margin: "14px auto 28px",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Our clinical team treats the full range of substance use disorders alongside co-occurring mental health conditions — serving patients from Glassboro, Cherry Hill, and all of South Jersey.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {conditions.map((c) => (
              <PillLink key={c.href + c.label} to={c.href}>
                {c.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — INSURANCE */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0 }}>
            Insurance Coverage for Glassboro Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              maxWidth: 680,
              margin: "16px auto 28px",
              lineHeight: 1.7,
              fontFamily: SANS,
            }}
          >
            We work with most major insurance plans held by New Jersey residents — including Aetna, Cigna, United Healthcare, Blue Cross Blue Shield, Anthem, Optum, Carelon, and Harvard Pilgrim. Under the Mental Health Parity Act, most plans are required to cover addiction treatment at the same level as other medical conditions. Our admissions team verifies your specific benefits before treatment begins at no cost — so you know exactly what your plan covers before committing to anything.
          </p>
          <Link
            to="/instant-insurance-verification-cherry-hill-nj"
            aria-label="Verify insurance in 60 seconds"
            style={{
              display: "inline-block",
              background: "#8B1A1A",
              color: "#FAF4F0",
              padding: "12px 28px",
              borderRadius: 999,
              fontWeight: 700,
              textDecoration: "none",
              fontSize: 14,
              fontFamily: SANS,
              marginBottom: 24,
            }}
          >
            ⚡ Verify Insurance Now →
          </Link>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginBottom: 32 }}>
            <InsurancePills insurers={insurers} style={{ justifyContent: "center" }} />
          </div>
          <Link
            to="/verify-insurance"
            aria-label="Verify your insurance — free"
            style={{
              display: "inline-block",
              background: "#8B1A1A",
              color: "#FAF4F0",
              padding: "14px 36px",
              borderRadius: 999,
              fontWeight: 700,
              textDecoration: "none",
              fontSize: 15,
              fontFamily: SANS,
            }}
          >
            Verify My Insurance — Free
          </Link>
        </div>
      </section>

      {/* SECTION 8 — NEARBY AREAS */}
      <section style={{ background: "#FFFFFF", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 26, margin: 0 }}>
            Also Serving Communities Near Glassboro
          </h2>
          <p style={{ color: "#555", fontSize: 15, marginTop: 14, marginBottom: 20, fontFamily: SANS }}>
            Cherry Hill Recovery Center serves patients from throughout Gloucester County, South Jersey, and the greater Philadelphia region.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {nearby.map((n) => (
              <PillLink key={n.href + n.label} to={n.href}>
                {n.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — MAP */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 24, margin: 0, marginBottom: 20 }}>
            Our Location — 18 Miles from Glassboro, NJ
          </h2>
          <LazyMap src="https://www.google.com/maps?q=Glassboro+NJ+08028&z=11&output=embed" title="Cherry Hill Recovery Center — serving Glassboro NJ 08028" height={340} />
          <div style={{ marginTop: 16, fontFamily: SANS }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#2C2C2C" }}>Cherry Hill Recovery Center</div>
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              1930 Marlton Pike East, Cherry Hill, NJ 08003
            </div>
            <a
              href="https://maps.google.com/maps?saddr=Glassboro+NJ+08028&daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none", display: "inline-block", marginTop: 10 }}
            >
              Get Directions from Glassboro →
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, marginBottom: 24 }}>
            Frequently Asked Questions — Addiction Treatment Near Glassboro, NJ
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                style={{
                  background: "#FFFFFF",
                  border: 0,
                  borderBottom: "1px solid #F0E0E0",
                  borderRadius: 0,
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                <AccordionTrigger className="hover:no-underline py-5 text-left">
                  <h3
                    style={{
                      color: "#2C2C2C",
                      fontSize: 16,
                      fontWeight: 600,
                      margin: 0,
                      fontFamily: SANS,
                    }}
                  >
                    {f.q}
                  </h3>
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    background: "#FAF4F0",
                    color: "#2C2C2C",
                    fontSize: 15,
                    lineHeight: 1.7,
                    padding: "16px 20px",
                    borderRadius: 8,
                    fontFamily: SANS,
                  }}
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 11 — BOTTOM CTA */}
      <section
        style={{
          background: "linear-gradient(135deg, #6B1414 0%, #8B1A1A 100%)",
          padding: "64px 20px",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 36, color: "#FFFFFF", margin: 0, lineHeight: 1.2 }}>
            Ready to Get Help Near Glassboro?
          </h2>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 18,
              maxWidth: 600,
              margin: "16px auto 0",
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            Cherry Hill Recovery Center is 18 miles from Glassboro via Route 322 and Route 295. Call 856-200-3127 for a free confidential assessment — available 24 hours a day. Evening and Virtual IOP available for Gloucester County patients and Rowan University students.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 24 }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE} →
            </a>
            <Link
              to="/instant-insurance-verification-cherry-hill-nj"
              aria-label="Verify insurance in 60 seconds"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              ⚡ Verify Insurance in 60 Seconds →
            </Link>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 18,
              marginTop: 24,
              color: "rgba(250,200,200,0.9)",
              fontSize: 13,
              fontFamily: SANS,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Lock size={14} /> 100% Confidential
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Check size={14} /> Insurance Accepted
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Phone size={14} /> Same-Day Response
            </span>
          </div>
        </div>
      </section>

      <RelatedLinks exclude={["/addiction-treatment-glassboro-nj"]} towns={["Sewell", "Turnersville", "Washington Township", "Cherry Hill"]} />

      <Footer />
      <FloatingCTAs />

      <style>{`
        @media (max-width: 900px) {
          .local-intro-grid { grid-template-columns: 1fr !important; }
          .local-intro-grid aside { position: static !important; }
        }
      `}</style>
    </div>
  );
};

export default AddictionTreatmentGlassboroNJ;
